// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
// helix-exception: inline text highlight marks keep their 1.15px/3px mark radii; no Helix radius token fits mark geometry (F-12)
import type { ClinicalSummaryReview, ClinicalSummaryReviewState } from '@/pages/order/clinicalSummaryReviewTypes'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'

export type HighlightTone = 'key' | 'additional' | 'excluded' | 'needs-input'

/** A removed term's phrase keeps a mark, but not a highlight: nothing to fill, reveal or describe. */
export type SummaryMarkTone = HighlightTone | 'removed'

export interface SummaryHighlightSpan {
  begin: number
  end: number
  tone: SummaryMarkTone
  code: string
}

/**
 * Marks sit under the textarea's own glyphs, so they may only paint a fill: a text colour ghosts
 * the glyphs, and a border or padding pushes the following text out of line with the textarea.
 * `text-inherit` keeps the backdrop's transparent text over the user agent's `mark` colour.
 * Figma's 2px padding is a same-colour spread shadow, and the needs-input stroke is a 1.5px ring
 * outside it. A spread grows the corner it paints, so the 1.15px radius is what comes out as
 * Figma's 3px corner on the padded box.
 */
export const highlightToneClass: Record<HighlightTone, string> = {
  key: 'box-decoration-clone rounded-[1.15px] bg-background-brand-soft dark:bg-gray-800 text-inherit shadow-[0_0_0_2px_var(--color-brand-100)]',
  additional:
    'box-decoration-clone rounded-[1.15px] bg-background-brand-soft dark:bg-gray-800 text-inherit shadow-[0_0_0_2px_var(--color-brand-100)]',
  excluded:
    'box-decoration-clone rounded-[1.15px] bg-error-100 dark:bg-gray-900 text-inherit shadow-[0_0_0_2px_var(--color-error-100)]',
  'needs-input':
    'box-decoration-clone rounded-[1.15px] bg-yellow-200 dark:bg-yellow-900/40 text-inherit shadow-[0_0_0_2px_var(--color-yellow-200),0_0_0_3.5px_var(--color-orange-500)]',
}

/**
 * Figma's 1.5px dashed bottom stroke on a removed phrase, drawn as a text decoration for the reason
 * above. Its colour is its own, so the transparent backdrop text does not hide it.
 */
export const removedMarkClass =
  'bg-transparent text-inherit underline decoration-dashed decoration-text-subtle decoration-[1.5px] underline-offset-[5px] [text-decoration-skip-ink:none]'

/** Figma's "hover linked" highlight, whatever the tone: brand-soft fill, its 2px brand stroke over the padding. */
export const highlightLinkedClass =
  'box-decoration-clone rounded-[1.15px] bg-background-brand-soft dark:bg-gray-800 text-inherit shadow-[0_0_0_2px_var(--color-brand-700)]'

/**
 * `highlightToneClass` for a mark the last extraction added: the same paint, with the fill as a
 * background image so it can grow left to right. Callers stagger it with `animation-delay`.
 * `bg-transparent` keeps the user agent's yellow `mark` colour out of the part not yet filled.
 */
export const highlightRevealToneClass: Record<HighlightTone, string> = {
  key: 'box-decoration-clone rounded-[1.15px] bg-transparent bg-[linear-gradient(var(--color-bg-brand-soft),var(--color-bg-brand-soft))] bg-[length:100%_100%] bg-no-repeat text-inherit shadow-[0_0_0_2px_var(--color-brand-100)] motion-safe:animate-[clinical-summary-mark-fill_0.5s_cubic-bezier(0.2,0.7,0.2,1)_backwards]',
  additional:
    'box-decoration-clone rounded-[1.15px] bg-transparent bg-[linear-gradient(var(--color-bg-brand-soft),var(--color-bg-brand-soft))] bg-[length:100%_100%] bg-no-repeat text-inherit shadow-[0_0_0_2px_var(--color-brand-100)] motion-safe:animate-[clinical-summary-mark-fill_0.5s_cubic-bezier(0.2,0.7,0.2,1)_backwards]',
  excluded:
    'box-decoration-clone rounded-[1.15px] bg-transparent bg-[linear-gradient(var(--color-error-100),var(--color-error-100))] bg-[length:100%_100%] bg-no-repeat text-inherit shadow-[0_0_0_2px_var(--color-error-100)] motion-safe:animate-[clinical-summary-mark-fill_0.5s_cubic-bezier(0.2,0.7,0.2,1)_backwards]',
  'needs-input':
    'box-decoration-clone rounded-[1.15px] bg-transparent bg-[linear-gradient(var(--color-yellow-200),var(--color-yellow-200))] bg-[length:100%_100%] bg-no-repeat text-inherit shadow-[0_0_0_2px_var(--color-yellow-200),0_0_0_3.5px_var(--color-orange-500)] motion-safe:animate-[clinical-summary-mark-fill_0.5s_cubic-bezier(0.2,0.7,0.2,1)_backwards]',
}

/** The mark tone of a term: needs-input outranks its category. */
export function highlightTone(
  reviewState: ClinicalSummaryReviewState | undefined,
  category: CategorizedHPOOption['category'] | null | undefined
): HighlightTone {
  if (reviewState === 'needs-input') return 'needs-input'
  if (category === 'Excluded') return 'excluded'
  if (category === 'Additional') return 'additional'
  return 'key'
}

function toneForTerm(
  code: string,
  clinicalDiagnosis: CategorizedHPOOption[],
  review?: ClinicalSummaryReview | null
): HighlightTone {
  return highlightTone(review?.terms[code]?.reviewState, clinicalDiagnosis.find((term) => term.id === code)?.category)
}

/**
 * Build non-overlapping spans from review citations (first-wins on overlap). A removed term's phrase
 * is marked only where no term still on the order cites the same words.
 */
export function buildSummaryHighlightSpans(
  summary: string,
  clinicalDiagnosis: CategorizedHPOOption[],
  review?: ClinicalSummaryReview | null
): SummaryHighlightSpan[] {
  const live: SummaryHighlightSpan[] = []
  const removed: SummaryHighlightSpan[] = []
  for (const [code, termReview] of Object.entries(review?.terms ?? {})) {
    const isRemoved = termReview.reviewState === 'removed'
    const tone = isRemoved ? 'removed' : toneForTerm(code, clinicalDiagnosis, review)
    for (const { begin, end } of termReview.citations) {
      if (!Number.isInteger(begin) || !Number.isInteger(end) || begin < 0 || end > summary.length || begin >= end) {
        continue
      }
      ;(isRemoved ? removed : live).push({ begin, end, tone, code })
    }
  }
  const liveSpans = withoutOverlaps(live)
  const freeRemoved = removed.filter((span) =>
    liveSpans.every((other) => span.end <= other.begin || span.begin >= other.end)
  )
  return withoutOverlaps([...liveSpans, ...freeRemoved])
}

function withoutOverlaps(candidates: SummaryHighlightSpan[]): SummaryHighlightSpan[] {
  const sorted = [...candidates].sort((a, b) => a.begin - b.begin || a.end - b.end)
  const spans: SummaryHighlightSpan[] = []
  let cursor = 0
  for (const span of sorted) {
    if (span.begin < cursor) continue
    spans.push(span)
    cursor = span.end
  }
  return spans
}

/** The single region `from` and `to` differ in: `[start, fromEnd)` of `from` became `[start, toEnd)` of `to`. */
function changedRegion(from: string, to: string): { start: number; fromEnd: number; toEnd: number } {
  let prefix = 0
  while (prefix < from.length && prefix < to.length && from[prefix] === to[prefix]) prefix += 1
  let suffix = 0
  while (
    suffix < from.length - prefix &&
    suffix < to.length - prefix &&
    from[from.length - 1 - suffix] === to[to.length - 1 - suffix]
  ) {
    suffix += 1
  }
  return { start: prefix, fromEnd: from.length - suffix, toEnd: to.length - suffix }
}

interface TextRange {
  begin: number
  end: number
}

/**
 * Carry spans built against `from` over to `to`: spans before the edit keep their offsets, spans
 * after it shift by the length change, and spans the edit touches are dropped.
 */
export function remapSpans<T extends TextRange>(spans: T[], from: string, to: string): T[] {
  if (from === to) return spans
  const { start, fromEnd } = changedRegion(from, to)
  const delta = to.length - from.length
  return spans.flatMap((span) => {
    if (span.end <= start) return [span]
    if (span.begin >= fromEnd) return [{ ...span, begin: span.begin + delta, end: span.end + delta }]
    return []
  })
}

const MAX_EDIT_TRAIL = 32

/**
 * Texts from `base` (the text the spans index) to `text`, one step per edit location. A single
 * diff from `base` treats everything between two separate edits as changed; remapping step by step
 * keeps the marks in between. Steps whose regions touch merge, so typing a word stays one step.
 * Returns `trail` itself when nothing changed.
 */
export function extendEditTrail(trail: readonly string[], base: string, text: string): readonly string[] {
  if (trail[0] !== base) return base === text ? [base] : [base, text]
  const last = trail[trail.length - 1]
  if (last === text) return trail
  if (text === base) return [base]
  if (trail.length >= 2) {
    const previous = trail[trail.length - 2]
    if (text === previous) return trail.slice(0, -1)
    const before = changedRegion(previous, last)
    const next = changedRegion(last, text)
    if (next.start <= before.toEnd && next.fromEnd >= before.start) return [...trail.slice(0, -1), text]
  }
  const extended = [...trail, text]
  // Dropping an intermediate text only widens the next step's diff, which drops marks, never misplaces them.
  if (extended.length > MAX_EDIT_TRAIL) extended.splice(1, 1)
  return extended
}

/** `remapSpans` along each step of an `extendEditTrail` trail. */
export function remapSpansAlongTrail<T extends TextRange>(spans: T[], trail: readonly string[]): T[] {
  let current = spans
  for (let step = 1; step < trail.length; step += 1) current = remapSpans(current, trail[step - 1], trail[step])
  return current
}

function endsSentenceAt(text: string, index: number): boolean {
  if (!/[.!?]/.test(text[index] ?? '')) return false
  let next = index + 1
  if (next < text.length && !/\s/.test(text[next])) return false
  while (next < text.length && /\s/.test(text[next])) next += 1
  return next === text.length || /[\p{Lu}"“]/u.test(text[next])
}

/**
 * Bounds of the sentence holding `[begin, end)`. Sentences break at newlines and at
 * `.`/`!`/`?` followed by whitespace and a capital, so initials ("Baby J.R. is") do not split.
 */
export function sentenceBounds(summary: string, begin: number, end: number): { start: number; end: number } {
  let start = begin
  while (start > 0 && summary[start - 1] !== '\n' && !endsSentenceAt(summary, start - 1)) start -= 1
  let stop = Math.max(begin, end - 1)
  while (stop < summary.length && summary[stop] !== '\n' && !endsSentenceAt(summary, stop)) stop += 1
  if (stop < summary.length && summary[stop] !== '\n') stop += 1
  while (start < stop && /\s/.test(summary[start])) start += 1
  while (stop > start && /\s/.test(summary[stop - 1])) stop -= 1
  return { start, end: stop }
}

/** Unclipped sentence holding the first in-range citation; null when the term has none. */
export function citationSentence(
  summary: string,
  citations: Array<{ begin: number; end: number }> | undefined
): string | null {
  const first = citations?.find(({ begin, end }) => begin >= 0 && begin < end && end <= summary.length)
  if (!first) return null
  const bounds = sentenceBounds(summary, first.begin, first.end)
  return summary.slice(bounds.start, bounds.end)
}

/**
 * The sentence around the first citation. A long lead-in is cut at a word start at most
 * `maxBefore` characters before the match and marked with a leading "…"; the rest of the sentence
 * follows unless it runs past `maxAfter`.
 */
export function citationSnippet(
  summary: string,
  citations: Array<{ begin: number; end: number }> | undefined,
  maxBefore = 68,
  maxAfter = 136
): { before: string; match: string; after: string } | null {
  // Stale offsets survive an edit, so skip to the first citation the summary can still quote —
  // the same choice `citationSentence` makes, so a row's quote and its sentence stay in step.
  const first = citations?.find(({ begin, end }) => begin >= 0 && begin < end && end <= summary.length)
  if (!first) return null
  const { begin, end } = first
  const sentence = sentenceBounds(summary, begin, end)
  let from = Math.min(begin, sentence.start)
  if (begin - from > maxBefore) {
    from = begin - maxBefore
    while (from < begin && /\S/.test(summary[from - 1])) from += 1
  }
  let to = Math.max(end, sentence.end)
  if (to - end > maxAfter) {
    to = end + maxAfter
    while (to > end && /\S/.test(summary[to])) to -= 1
  }
  return {
    before: `${from > sentence.start ? '…' : ''}${summary.slice(from, begin)}`,
    match: summary.slice(begin, end),
    after: `${summary.slice(end, to).trimEnd()}${to < sentence.end ? '…' : ''}`,
  }
}
